/**
 * Extracts a string from a value that may be a locale object (e.g. {en_US, es_ES}).
 * Use this before rendering values that might come from locale_titles or locale_descriptions.
 * @param {string|object|null|undefined} value - A string, or an object with locale keys like en_US, es_ES
 * @param {string} [locale] - Preferred locale (e.g. 'es_ES', 'en_US')
 * @returns {string} A string safe for React rendering
 */
export const getLocaleString = (value, locale = 'es_ES') => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && !Array.isArray(value)) {
      return value[locale] ?? value.es_ES ?? value.en_US ?? Object.values(value)[0] ?? '';
    }
    return String(value);
  };

// Maps the short language code stored by LanguageSelect/App.js (localStorage 'locale')
// to the long-form key used throughout the DB/GraphQL layer (course.default_language,
// evaluation_items.name, locale_titles, locale_descriptions, etc). Comparing the short
// code directly against those keys (e.g. 'en' vs 'en_US') never matches, which is the
// recurring bug this module exists to stop reintroducing.
const SHORT_TO_LONG_LOCALE = { es: 'es_ES', en: 'en_US', tr: 'tr_TR', pt: 'pt_PT' };

/**
 * Normalizes a locale code to its long form (es -> es_ES). Already-long codes and
 * unrecognized values pass through unchanged.
 * @param {string|null|undefined} rawLocale
 * @returns {string}
 */
export const normalizeLocale = (rawLocale) => {
  if (!rawLocale) return 'es_ES';
  return SHORT_TO_LONG_LOCALE[rawLocale] || rawLocale;
};

/**
 * Reads the user's currently selected language from localStorage and normalizes it
 * to the long form used by locale-keyed data (es_ES, en_US, tr_TR, pt_PT).
 * @returns {string}
 */
export const getCurrentLocale = () => {
  const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('locale') : null;
  return normalizeLocale(raw);
};

/**
 * Resolves a locale-keyed field (evaluation_items.name, locale_titles, locale_descriptions,
 * aditional_text, etc.) to a plain string for the CURRENT user, using localStorage's
 * 'locale' directly (normalized). Accepts either an already-parsed object or a JSON string.
 *
 * Fallback order: user's language -> contentLocale (course.default_language, if passed) ->
 * es_ES -> en_US -> first non-empty value found. This mirrors the backend's intended
 * fallback (evaluation_item_type.rb) but always tries the USER's language first, which is
 * what the backend gate on `available_languages` sometimes fails to do.
 *
 * @param {string|object|null|undefined} value
 * @param {{ contentLocale?: string }} [options] - course.default_language, for content-level fallback
 * @returns {string}
 */
export const resolveLocalizedField = (value, { contentLocale } = {}) => {
  if (value == null) return '';

  let obj = value;
  if (typeof value === 'string') {
    if (!value.trim().startsWith('{')) return value;
    try {
      obj = JSON.parse(value);
    } catch {
      return value;
    }
  }

  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return typeof value === 'string' ? value : '';
  }

  const userLocale = getCurrentLocale();
  const normalizedContentLocale = contentLocale ? normalizeLocale(contentLocale) : null;

  return (
    obj[userLocale] ||
    (normalizedContentLocale ? obj[normalizedContentLocale] : '') ||
    obj.es_ES ||
    obj.en_US ||
    Object.values(obj).find((v) => typeof v === 'string' && v.trim().length > 0) ||
    ''
  );
};